import { useMutation, useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";

export const RadarCheckContext = createContext(null);

type Props = {
  children: ReactElement;
};

const RadarCheckProvider: FunctionComponent<Props> = ({ children }) => {
  const {
    mutateAsync: individualCheckMutateAsync,
    data: individualCheckData,
    isPending: individualCheckIsPending,
    error: individualCheckError,
    reset: individualResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    mutateAsync: businessCheckMutateAsync,
    data: businessCheckData,
    isPending: businessCheckIsPending,
    error: businessCheckError,
    reset: businessResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/business/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    data: verificationEndpointData,
    isLoading: verificationEndpointIsLoading,
    isFetching: verificationEndpointIsFetching,
  } = useQuery({
    queryKey: ["identity_verification"],
    queryFn: async () => {
      const {
        data: { data },
      } = await Api.get(`onboarding/kyc/endpoints`);

      return data;
    },
  });

  const {
    mutateAsync: kycMutateAsync,
    data: kycData,
    isPending: kycIsPending,
    error: kycError,
    reset: kycResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/kyc/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    mutateAsync: bulkCheckMutateAsync,
    data: bulkCheckData,
    isPending: bulkCheckIsPending,
    error: bulkCheckError,
    reset: bulkResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/bulk/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    mutateAsync: businessBulkCheckMutateAsync,
    data: businessBulkCheckData,
    isPending: businessBulkCheckIsPending,
    error: businessBulkCheckError,
    reset: businessBulkResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/bulk/business/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    mutateAsync: kycBulkCheckMutateAsync,
    data: kycBulkCheckData,
    isPending: kycBulkCheckIsPending,
    error: kycBulkCheckError,
    reset: kycBulkResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/bulk/kyc/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
      // toast({
      //   description: "Server Error",
      //   status: "error",
      //   duration: 9000,
      //   isClosable: true,
      //   variant: "left-accent",
      // });
    },
  });

  const {
    mutateAsync: individualSocialScreeningCheckMutateAsync,
    data: individualSocialScreeningCheckData,
    isPending: individualSoocialScreeningCheckIsPending,
    error: individualSocialScreeningCheckError,
    reset: individualSocialScreeningResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/online-profile/verify", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const {
    mutateAsync: individualMismatchMutateAsync,
    data: individualMismatchCheckData,
    isPending: individualMismatchIsPending,
    error: individualMismatchCheckError,
    reset: individualMismatchResetData,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await Api.post("onboarding/mismatch-retry", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const contextValue = useMemo(
    () => ({
      individualCheckMutateAsync,
      individualCheckData,
      individualCheckError,
      individualCheckIsPending,
      individualResetData,
      businessCheckMutateAsync,
      businessCheckData,
      businessCheckIsPending,
      businessCheckError,
      businessResetData,
      verificationEndpointData,
      verificationEndpointIsFetching,
      verificationEndpointIsLoading,
      kycMutateAsync,
      kycData,
      kycError,
      kycIsPending,
      kycResetData,
      bulkCheckMutateAsync,
      bulkCheckData,
      bulkCheckIsPending,
      bulkCheckError,
      bulkResetData,
      businessBulkCheckMutateAsync,
      businessBulkCheckData,
      businessBulkCheckError,
      businessBulkCheckIsPending,
      businessBulkResetData,
      kycBulkCheckMutateAsync,
      kycBulkCheckData,
      kycBulkCheckError,
      kycBulkCheckIsPending,
      kycBulkResetData,
      individualSocialScreeningCheckMutateAsync,
      individualSocialScreeningCheckData,
      individualSoocialScreeningCheckIsPending,
      individualSocialScreeningCheckError,
      individualSocialScreeningResetData,
      individualMismatchMutateAsync,
      individualMismatchCheckData,
      individualMismatchIsPending,
      individualMismatchCheckError,
      individualMismatchResetData,
    }),
    [
      individualCheckMutateAsync,
      individualCheckData,
      individualCheckError,
      individualCheckIsPending,
      individualResetData,
      businessCheckMutateAsync,
      businessCheckData,
      businessCheckIsPending,
      businessCheckError,
      businessResetData,
      verificationEndpointData,
      verificationEndpointIsFetching,
      verificationEndpointIsLoading,
      kycMutateAsync,
      kycData,
      kycError,
      kycIsPending,
      kycResetData,
      bulkCheckMutateAsync,
      bulkCheckData,
      bulkCheckIsPending,
      bulkCheckError,
      bulkResetData,
      businessBulkCheckMutateAsync,
      businessBulkCheckData,
      businessBulkCheckError,
      businessBulkCheckIsPending,
      businessBulkResetData,
      kycBulkCheckMutateAsync,
      kycBulkCheckData,
      kycBulkCheckError,
      kycBulkCheckIsPending,
      kycBulkResetData,
      individualSocialScreeningCheckMutateAsync,
      individualSocialScreeningCheckData,
      individualSoocialScreeningCheckIsPending,
      individualSocialScreeningCheckError,
      individualSocialScreeningResetData,
      individualMismatchMutateAsync,
      individualMismatchCheckData,
      individualMismatchIsPending,
      individualMismatchCheckError,
      individualMismatchResetData,
    ],
  );
  return (
    <RadarCheckContext.Provider value={contextValue}>
      {children}
    </RadarCheckContext.Provider>
  );
};

export default RadarCheckProvider;
